import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/shadcn/card";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Badge } from "@/components/shadcn/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { adminApi, adminUsersQueryOptions } from "@/api/admin/admin";
import { ASSIGNABLE_ROLES } from "@/api/admin/admin.types";

export function UserRoleManagement() {
    const [emailFilter, setEmailFilter] = useState("");
    const [pendingRole, setPendingRole] = useState<Record<number, string>>({});
    const [pendingReadOnly, setPendingReadOnly] = useState<Record<number, boolean>>({});
    const queryClient = useQueryClient();

    const { data: users, isLoading } = useQuery(adminUsersQueryOptions(emailFilter || undefined));

    const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

    const grantMutation = useMutation({
        mutationFn: adminApi.grantRole,
        onSuccess: () => {
            toast.success("Role granted");
            invalidateUsers();
        },
        onError: () => toast.error("Failed to grant role"),
    });

    const revokeMutation = useMutation({
        mutationFn: adminApi.revokeRole,
        onSuccess: () => {
            toast.success("Role revoked");
            invalidateUsers();
        },
        onError: () => toast.error("Failed to revoke role"),
    });

    return (
        <Card>
            <CardHeader>
                <CardTitle>User roles</CardTitle>
            </CardHeader>
            <CardContent>
                <Input
                    placeholder="Search by email..."
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    className="max-w-sm mb-4"
                />

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Username</TableHead>
                            <TableHead>Roles</TableHead>
                            <TableHead>Grant role</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={4}>Loading...</TableCell>
                            </TableRow>
                        )}
                        {!isLoading && users?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4}>No users found.</TableCell>
                            </TableRow>
                        )}
                        {users?.map((user) => (
                            <TableRow key={user.userId}>
                                <TableCell>{user.email ?? "—"}</TableCell>
                                <TableCell>{user.username ?? "—"}</TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {user.roles.length === 0 && (
                                            <span className="text-on-surface-variant">No roles</span>
                                        )}
                                        {user.roles.map((role) => (
                                            <Badge key={role.roleName} variant="secondary" className="gap-1.5">
                                                {role.roleName}
                                                {role.isReadOnly && <span className="opacity-70">(read-only)</span>}
                                                <button
                                                    type="button"
                                                    className="opacity-60 hover:opacity-100 cursor-pointer"
                                                    disabled={revokeMutation.isPending}
                                                    onClick={() =>
                                                        revokeMutation.mutate({
                                                            userId: user.userId,
                                                            roleName: role.roleName,
                                                        })
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <Select
                                            value={pendingRole[user.userId] ?? ""}
                                            onValueChange={(value) =>
                                                setPendingRole((prev) => ({ ...prev, [user.userId]: value }))
                                            }
                                        >
                                            <SelectTrigger size="sm" className="w-40">
                                                <SelectValue placeholder="Role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ASSIGNABLE_ROLES.map((role) => (
                                                    <SelectItem key={role} value={role}>
                                                        {role}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <label className="flex items-center gap-1 text-xs text-on-surface-variant">
                                            <Checkbox
                                                checked={pendingReadOnly[user.userId] ?? false}
                                                onCheckedChange={(checked) =>
                                                    setPendingReadOnly((prev) => ({
                                                        ...prev,
                                                        [user.userId]: checked === true,
                                                    }))
                                                }
                                            />
                                            Read-only
                                        </label>
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            disabled={!pendingRole[user.userId] || grantMutation.isPending}
                                            onClick={() =>
                                                grantMutation.mutate({
                                                    userId: user.userId,
                                                    roleName: pendingRole[user.userId],
                                                    isReadOnly: pendingReadOnly[user.userId] ?? false,
                                                })
                                            }
                                        >
                                            Grant
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
